import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Cell } from 'react-table';
import { Alert, Button, Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router';
import { rrulestr } from 'rrule';
import { useMount, useUpdateEffect } from 'react-use';

import { getPath } from 'routes';
import { Pencil2Icon, TrashcanIcon } from 'assets/icons';
import { IconTooltip, Table, TruncateTxt } from 'components/UI';
import {
  PatientStatus,
  PatientTabs,
  TaskStatus,
  TaskTimeFilter,
  TaskTriggerEvent
} from 'constants/enums';
import { Error } from 'components/Fetch';
import { statusToString, TABLE_EMPTY_VALUE } from 'constants/constants';
import { isLegacyRecurrence, RecurrenceValue } from 'utils/RecurrenceEngine';
import { getTasks, getTasksForTemplate } from 'store/tasks/service';
import { useControlledTableSelection } from 'hooks';
import { formatCarePrograms } from 'utils/carePrograms';
import { formatAssigneesNames } from 'utils/Groups';
import { clearTasksSelection } from 'store/clinicalLogger/reducer';
import { getIconColorFromStatus, getIconFromStatus, hasInvalidSelection } from 'utils/tasks';
import { getAllGids, getInterventions, selectCurrentGid } from 'store/groups/reducer';
import { updateFilters } from 'store/tasks/reducer';
import { formatTimeStringForRecurrence } from 'utils/Date';
import { useAppSelector } from 'hooks';

import { ConditionsView, UsersColumnView } from '../Table';
import TasksFilters from './TasksFilters';
import { ActiveFiltersDisplay, Search } from '../Filters';
import { BulkTaskActions } from './BulkActions';

interface ITasksTableProps {
  variant?: 'all' | 'patient' | 'careplan';
  targetTasks?: ITask[] | null;
  handleAction?: (action: ModalType, task: ITask) => void;
  carePlanTemplate?: ICarePlanTemplate;
  carePlan?: ICarePlan;
  disabled?: boolean;
}

const TasksTable = ({
  variant = 'all',
  targetTasks,
  handleAction,
  carePlanTemplate,
  carePlan,
  disabled
}: ITasksTableProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locale = useAppSelector(({ intl }) => intl.locale);
  const gid = useAppSelector(selectCurrentGid);
  const allGids = useAppSelector(getAllGids);
  const savedFilters = useAppSelector((state) => state.tasks.filters);
  const interventions = useAppSelector(getInterventions);
  const patient = useAppSelector((state) => state.patient?.data);
  const { data, error, loading } = useAppSelector((state) => state.tasks);
  const isAllTasksView = variant === 'all';

  const [filters, setFilters] = useState<ITemplatePageFilters[]>(() => {
    return savedFilters || [];
  });
  const [search, setSearch] = useState<string | null>(null);
  const [sort, setSort] = useState({ id: 'occurenceDate', desc: false });
  const isTemplateGlobal = carePlanTemplate?.template_type === 'GLOBAL';
  const { controlledCol } = useControlledTableSelection('tasks');

  const get = () => {
    if (variant === 'careplan' && patient?.uid === undefined && carePlanTemplate === undefined)
      return;
    dispatch(
      carePlanTemplate !== undefined
        ? getTasksForTemplate({ gid: gid, templateId: carePlanTemplate.template_id })
        : getTasks({
            gids: variant !== 'all' ? undefined : gid === 'all' ? allGids : [gid],
            uid: variant !== 'all' ? patient?.uid : undefined
          })
    );
  };

  const getInterventionById = (id: string) => {
    return (
      interventions.managed.find((intervention) => intervention.id === id) ||
      Object.values(interventions.userCreated)
        .flat()
        .find((intervention: any) => intervention.id === id)
    );
  };

  const extractOccurrenceDate = (task: ITask) => {
    if (task.recurrence.rrule && task.status !== parseInt(TaskStatus.ON_HOLD)) {
      let date = null;
      if (task.status === parseInt(TaskStatus.ACTIVE)) {
        date = new Date(task.nextOccurrence);
      } else {
        date = new Date(task.lastOccurrence);
      }
      return date && date.getTime() > 0 ? DateTime.fromJSDate(date) : null;
    }
    return null;
  };

  const formatTriggerDate = (task: ITask) => {
    let dateString = '';
    if (task.trigger) {
      dateString = getTriggerCause(task.trigger.event, task.trigger.details);
    } else if (carePlanTemplate) {
      dateString = task.startAfter
        ? `${formatMessage({ id: 'afterDaysKey' }, { days: task.startAfter.days })}`
        : TABLE_EMPTY_VALUE;
    } else {
      dateString = task.startDate
        ? DateTime.fromMillis(task.startDate).setLocale(locale).toLocaleString()
        : TABLE_EMPTY_VALUE;
    }

    return dateString;
  };

  const getTriggerCause = (event: TaskTriggerEvent, details: ITaskTriggerDetails) => {
    let cause = '';
    switch (event) {
      case TaskTriggerEvent.ON_STATUS_CHANGE: {
        const targetStatus = statusToString(details.value.toString() as PatientStatus);
        cause = `${details.startAfter.days} ${formatMessage({ id: 'tasks.trigger.days' })} ${formatMessage({ id: 'tasks.trigger.triggerStatus.cause' }, { status: formatMessage({ id: targetStatus }) })}`;
        break;
      }
      // add here other cases for questionnaire score or measurement value event triggers
      default:
        cause = event;
    }
    return cause;
  };

  const formatEndDate = (task: ITask) => {
    if (carePlanTemplate || task.trigger) {
      return task.endAfter
        ? `${formatMessage({ id: 'afterDaysKey' }, { days: task.endAfter.days })}`
        : TABLE_EMPTY_VALUE;
    } else {
      return task.endDate
        ? DateTime.fromMillis(task.endDate).setLocale(locale).toLocaleString()
        : TABLE_EMPTY_VALUE;
    }
  };

  const handleFilters = () => {
    if (error) return [];
    const tasks = targetTasks ? targetTasks : data || [];
    if (patient) return tasks;

    return [...tasks].filter((task) => {
      let isFilteredIn = true;
      if (search) {
        const patientName = task.patientInfo.firstName + ' ' + task.patientInfo.lastName;
        const patientNameReverse = task.patientInfo.lastName + ' ' + task.patientInfo.firstName;
        isFilteredIn =
          patientName.toLowerCase().includes(search.toLowerCase()) ||
          patientNameReverse.toLowerCase().includes(search.toLowerCase());
        if (!isFilteredIn) return false;
      }
      for (const filter of filters) {
        const filterValue = filter.filterValue;
        switch (filter.filterLabel) {
          case 'taskStatus':
            isFilteredIn = filterValue?.includes(task.status?.toString()) ?? false;
            break;
          case 'assignUsers':
            isFilteredIn = !!filterValue?.filter((value) => task.assignees?.includes(value)).length;
            break;
          case 'subgroups':
            isFilteredIn = filterValue?.includes(task.patientInfo?.subgroup_gid) ?? false;
            break;
          case 'tags':
            isFilteredIn = !!filterValue?.filter((value) => task.patientInfo?.tags?.includes(value))
              .length;
            break;
          case 'patientStatuses':
            isFilteredIn = filterValue?.includes(task.patientInfo?.patientStatus) ?? false;
            break;
          case 'patientCareProgram':
            isFilteredIn = !!filterValue?.filter((value) =>
              task.patientInfo?.patientCareProgram?.includes(value)
            ).length;
            break;
          case 'timeLogType':
            {
              const min =
                filters.find((x) => x.filterLabel === 'timeLogMin')?.filterValue?.[0] || '0';
              const max =
                filters.find((x) => x.filterLabel === 'timeLogMax')?.filterValue?.[0] || undefined;
              isFilteredIn = !!filterValue?.filter((x) => {
                if (!task.patientInfo?.logTypes?.[0]) return false;
                return (
                  Object.keys(task.patientInfo.logTypes[0]).includes(x) &&
                  parseInt(task.patientInfo.logTypes[0][x]) > parseInt(min) * 60 &&
                  (max ? parseInt(task.patientInfo.logTypes[0][x]) < parseInt(max) * 60 : true)
                );
              }).length;
            }
            break;
          case 'taskTime':
            if (filterValue) {
              if (task.recurrence?.rrule) {
                if (
                  task.status !== parseInt(TaskStatus.DUE) &&
                  task.status !== parseInt(TaskStatus.OVERDUE)
                ) {
                  const nextOccurence = DateTime.fromJSDate(new Date(task.nextOccurrence));
                  switch (filterValue[0]) {
                    case TaskTimeFilter.TODAY:
                      isFilteredIn = nextOccurence.hasSame(DateTime.local(), 'day');
                      break;
                    case TaskTimeFilter.THIS_WEEK:
                      isFilteredIn = nextOccurence.hasSame(DateTime.local(), 'week');
                      break;
                    case TaskTimeFilter.NEXT_WEEK:
                      isFilteredIn = nextOccurence.hasSame(
                        DateTime.local().plus({ week: 1 }),
                        'week'
                      );
                      break;
                    case TaskTimeFilter.THIS_MONTH:
                      isFilteredIn = nextOccurence.hasSame(DateTime.local(), 'month');
                      break;
                    case TaskTimeFilter.NEXT_MONTH:
                      isFilteredIn = nextOccurence.hasSame(
                        DateTime.local().plus({ month: 1 }),
                        'month'
                      );
                      break;
                  }
                }
              }
            }
        }
        if (!isFilteredIn) return false;
      }
      return true;
    });
  };

  const columns: any = [
    {
      id: 'status',
      accessor: 'status',
      Header: () => formatMessage({ id: 'status' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        if (!original.status?.toString()) return <></>;
        const StatusIcon = getIconFromStatus(original.status.toString());
        return (
          <div className="d-flex justify-content-center">
            <IconTooltip overlay={formatMessage({ id: `tasks.status.${original.status}` })}>
              <StatusIcon
                className={`icon ${getIconColorFromStatus(original.status.toString())}`}
              />
            </IconTooltip>
          </div>
        );
      },
      align: 'center',
      maxWidth: 100,
      hide: variant === 'careplan'
    },
    {
      id: 'interventionId',
      accessor: (originalRow: ITask) => {
        const intervention = getInterventionById(originalRow.interventionId);
        return intervention?.name ? formatMessage({ id: intervention.name }) : TABLE_EMPTY_VALUE;
      },
      Header: () => formatMessage({ id: 'titleKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        const intervention = getInterventionById(original.interventionId);
        if (intervention?.name) {
          return (
            <OverlayTrigger
              overlay={
                <Tooltip id="tooltip-tasks-intervention-overlay">
                  {formatMessage({ id: intervention.name })}
                </Tooltip>
              }
            >
              <p className="m-0 clamp-text clamp-line-1">
                {formatMessage({ id: intervention.name })}
              </p>
            </OverlayTrigger>
          );
        } else {
          return TABLE_EMPTY_VALUE;
        }
      },
      minWidth: 350
    },
    {
      id: 'occurenceDate',
      accessor: 'occurenceDate',
      Header: () => formatMessage({ id: 'dateKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        const occurrence = extractOccurrenceDate(original);
        return occurrence
          ? occurrence?.setLocale(locale).toLocaleString(DateTime.DATETIME_SHORT)
          : TABLE_EMPTY_VALUE;
      },
      sortType: ({ original: taskA }: any, { original: taskB }: any, descending: boolean) => {
        const taskAOccurrence = extractOccurrenceDate(taskA);
        const taskBOccurrence = extractOccurrenceDate(taskB);
        if (taskAOccurrence === taskBOccurrence) return 0;
        // unscheduled always at the top of the list
        // reverse next 2 ifs to put them at the bottom
        else if (!taskAOccurrence) return descending ? 1 : -1;
        else if (!taskBOccurrence) return descending ? -1 : 1;
        else return taskAOccurrence.toMillis() - taskBOccurrence.toMillis();
      },
      align: 'right',
      hide: variant === 'careplan'
    },
    {
      id: 'startTime',
      accessor: 'startTime',
      Header: () => formatMessage({ id: 'timeKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        if (
          (isLegacyRecurrence(original.recurrence.rruleNameKey) ||
            original.recurrence.rruleNameKey === RecurrenceValue.ONE_TIME) &&
          original.recurrence.rrule
        ) {
          if (original.startTime)
            return (
              <>
                {DateTime.now()
                  .setLocale(locale)
                  .set(formatTimeStringForRecurrence(original.startTime))
                  .toLocaleString(DateTime.TIME_SIMPLE)}
              </>
            );
          const rule = rrulestr(original.recurrence.rrule);
          return (
            <>
              {rule.options.dtstart
                ? DateTime.fromJSDate(rule.options.dtstart)
                    .setLocale(locale)
                    .toUTC()
                    .toLocaleString(DateTime.TIME_SIMPLE)
                : ''}
            </>
          );
        } else {
          return TABLE_EMPTY_VALUE;
        }
      },
      align: 'right',
      hide: variant !== 'careplan'
    },
    {
      id: 'patient',
      accessor: 'patientInfo.lastName',
      Header: () => formatMessage({ id: 'patientKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => (
        <TruncateTxt
          id="name"
          txt={original.patientInfo.lastName + ', ' + original.patientInfo.firstName}
        />
      ),
      minWidth: 100,
      hide: variant !== 'all'
    },
    {
      id: 'alternateId',
      accessor: (originalRow: ITask) => originalRow.alternateId || '',
      Header: () => formatMessage({ id: 'alternateIdKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => original.alternateId || TABLE_EMPTY_VALUE,
      minWidth: 100,
      hide: variant !== 'all'
    },
    {
      id: 'group',
      accessor: (originalRow: ITask) => originalRow.patientInfo?.group_name || '',
      Header: () => formatMessage({ id: 'groupKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => (
        <TruncateTxt id="tooltip-group" txt={original.patientInfo?.group_name || ''} />
      ),
      minWidth: 100,
      hide: variant !== 'all'
    },
    {
      id: 'program',
      accessor: (originalRow: ITask) => formatCarePrograms(originalRow.patientInfo, true),
      Header: () => formatMessage({ id: 'careProgramsKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) =>
        formatCarePrograms(original.patientInfo, true) || TABLE_EMPTY_VALUE,
      hide: variant !== 'all'
    },
    {
      id: 'conditions',
      accessor: (originalRow: ITask) =>
        originalRow.patientInfo.conditions
          ?.map((condition: ICondition) => formatMessage({ id: condition.condition }))
          .toString() || '',
      Header: () => formatMessage({ id: 'conditionsKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => <ConditionsView data={original.patientInfo} />,
      hide: variant !== 'all'
    },
    {
      id: 'recurrence',
      accessor: 'recurrence',
      Header: () => formatMessage({ id: 'frequencyKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => {
        let text = null;
        if (isLegacyRecurrence(original.recurrence.rruleNameKey) && original.recurrence.rrule) {
          const rule = rrulestr(original.recurrence.rrule);
          text = rule.toText();
        } else {
          text = formatMessage({ id: original.recurrence.rruleNameKey });
        }
        return (
          <OverlayTrigger
            overlay={<Tooltip id="tooltip-tasks-intervention-overlay">{text}</Tooltip>}
          >
            <p className="m-0 clamp-text clamp-line-1">{text}</p>
          </OverlayTrigger>
        );
      },
      disableSortBy: true,
      minWidth: 200
    },
    {
      id: 'startDate',
      accessor: 'startDate',
      Header: () => formatMessage({ id: 'start' }),
      Cell: ({ row: { original } }: Cell<ITask>) => formatTriggerDate(original),
      align: 'right'
    },
    {
      id: 'endDate',
      accessor: 'endDate',
      Header: () => formatMessage({ id: 'endKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => formatEndDate(original),
      align: 'right'
    },
    // {
    //   id: 'lastOccurrence',
    //   accessor: 'lastOccurrence',
    //   Header: () => formatMessage({ id: 'lastResponseKey' }),
    //   Cell: ({ row: { original } }: Cell<ITask>) => {
    //     return original.lastOccurrence
    //       ? DateTime.fromMillis(original.lastOccurrence).toLocaleString()
    //       : TABLE_EMPTY_VALUE;
    //   },
    //   disableSortBy: true,
    //   minWidth: 100,
    // },
    {
      id: 'assignees',
      accessor: (originalRow: ITask) => formatAssigneesNames(originalRow.assignees),
      Header: () => formatMessage({ id: 'taskAssigneesKey' }),
      Cell: ({ row: { original } }: Cell<ITask>) => (
        <UsersColumnView admins={original.assignees} row={original} />
      ),
      minWidth: 200,
      align: 'center'
    }
  ];

  if (variant !== 'careplan') {
    columns.unshift(controlledCol);
  }

  if (handleAction) {
    columns.push({
      id: 'controls',
      Cell: ({ row: { original } }: Cell<ITask>) => {
        return (
          <div className="d-flex">
            <Button
              data-test={`tasks-table-actions-${original.id}-edit`}
              variant="link"
              onClick={() => handleAction('edit', original)}
              disabled={disabled}
            >
              <Pencil2Icon className="icon" style={{ fontSize: '14px' }} />
            </Button>
            <Button
              data-test={`tasks-table-actions-${original.id}-delete`}
              variant="link"
              onClick={() => handleAction('delete', original)}
              disabled={disabled}
            >
              <TrashcanIcon className="icon" style={{ fontSize: '14px' }} />
            </Button>
          </div>
        );
      },
      maxWidth: 150,
      disableSortBy: true,
      hide: variant !== 'careplan' || isTemplateGlobal
    });
  }

  const clearFilters = (filtersLabels: IFilterLabel[]) => {
    return setFilters(filters.filter((filter) => !filtersLabels.includes(filter.filterLabel)));
  };

  const SelectionWarning = () => {
    const selectedTasks = useAppSelector((state) => state.clinicalLogger.tasks);
    const invalidSelection = hasInvalidSelection(selectedTasks);
    const hasUpcomingTasks = selectedTasks.some(
      (task) => task.status === parseInt(TaskStatus.ACTIVE)
    );

    return (
      <>
        {invalidSelection ? (
          <Alert variant="warning">
            {formatMessage({ id: 'tasks.actions.closeAndDocument.warningV2' })}
          </Alert>
        ) : (
          hasUpcomingTasks && (
            <Alert variant="warning">
              {formatMessage({ id: 'tasks.actions.closeAndDocument.warningUpcoming' })}
            </Alert>
          )
        )}
      </>
    );
  };

  useUpdateEffect(() => {
    dispatch(updateFilters(filters));
    dispatch(clearTasksSelection());
  }, [filters, search]);

  useMount(() => {
    get();
    // Set default "Today" filter if no saved filters exist and we're showing filters
    if (isAllTasksView && (!savedFilters || savedFilters.length === 0)) {
      const defaultFilter: ITemplatePageFilters = {
        filterLabel: 'taskTime',
        filterData: [
          {
            label: formatMessage({ id: `tasks.timeFilter.${TaskTimeFilter.TODAY}` }),
            value: TaskTimeFilter.TODAY
          }
        ],
        filterValue: [TaskTimeFilter.TODAY]
      };
      setFilters([defaultFilter]);
    }
  });

  useUpdateEffect(() => {
    if (!loading) {
      get();
      if (!isAllTasksView) {
        setFilters([]);
      }
      dispatch(clearTasksSelection());
    }
  }, [gid, carePlanTemplate, patient, carePlan]);

  if (error) return <Error retry={get} />;

  const filteredData = handleFilters();

  return (
    <>
      <Container
        fluid
        className="bg-white pt-2 mb-3"
        data-test={`${variant === 'patient' ? 'patient-tasks' : ''}`}
      >
        {variant !== 'careplan' && <SelectionWarning />}
        {variant === 'careplan' && (loading || filteredData.length === 0) ? (
          <div
            className="bg-light text-center p-3 font-weight-bolder"
            data-test="careplan-tasks-empty"
          >
            {formatMessage({ id: 'noTasksSetKey' })}
          </div>
        ) : (
          <>
            {isAllTasksView && (
              <Row>
                <Col sm={7} className="mb-2">
                  <TasksFilters filters={filters} setFilters={setFilters} />
                  {(filters.length || 0) > 0 && (
                    <ActiveFiltersDisplay activeFilters={filters} clearFilters={clearFilters} />
                  )}
                </Col>
              </Row>
            )}
            <Table
              dataTestIdPrefix="tasks"
              onSortChange={(sort) => setSort(sort[0])}
              defaultSortColumn={sort}
              defaultPageSize={25}
              loading={loading}
              data={filteredData}
              columns={columns}
              disablePageSizeSelect={variant !== 'all'}
              pagination
              manualPagination={false}
              noDataTxt={formatMessage({ id: 'noTasksKey' })}
              leftComponent={
                <Container fluid className="mb-2 p-0">
                  <Row className="d-flex align-items-center">
                    <Col sm={8}>
                      {isAllTasksView && (
                        <Row>
                          <Col>
                            <Search
                              placeholder={`${formatMessage({
                                id: 'searchByKey'
                              })} ${formatMessage({
                                id: 'patientNameKey'
                              })}`}
                              handleSearch={(value) => setSearch(value)}
                              handleReset={() => setSearch(null)}
                              dataTestId="tasks-patients-search"
                              active={!!search}
                            />
                          </Col>
                        </Row>
                      )}
                    </Col>
                    <Col sm={4}>{variant !== 'careplan' && <BulkTaskActions get={get} />}</Col>
                  </Row>
                </Container>
              }
              onRowClicked={(row: any) => {
                if (variant === 'all') {
                  navigate(getPath(true).dashboard.patient(row.uid, PatientTabs.TASKS), {
                    state: { prevPath: getPath(false).dashboard.tasks() }
                  });
                }
              }}
              autoHeight={variant === 'all'}
              hideMargin={true}
              showPaginationIfMultiplePage={variant === 'careplan'}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default TasksTable;
